// @flow
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import type { PageProps } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'
import Helmet from 'react-helmet'

import { colors, fonts, media } from '@toggl/style'
import {
  Button,
  Hero,
  IconCheck,
  Responsive,
  Reviews,
  SectionBlock,
  text,
} from '@toggl/ui'
import DemoRequestForm from 'components/DemoRequestForm'
import { LayoutTrack } from 'components/layouts'

// eslint-disable-next-line no-empty-pattern
const DemoRequestPage = ({}: PageProps<void, void>) => {
  return (
    <LayoutTrack
      title={'Request a Free Team Time Tracking Demo | Toggl Track'}
      description={
        'Avoid scope creep, track project completion, and rapidly spot potential issues in your team’s workflow. See how Toggl can help your team stay on track.'
      }
      ogImage={require('assets/toggl-track-contact-us.jpg').default}
      noSmoothScrolling
    >
      <Helmet>
        <link rel='canonical' href='https://toggl.com/track/demo-request/' />
      </Helmet>
      <Hero.AltWrapper>
        <SplitContent>
          <StyledLeft>
            <text.H1 color={colors.peach50} size='medium'>
              Book your <Rotalic>free demo</Rotalic> of Toggl Track
            </text.H1>

            <ListPretext color={colors.peach50}>
              <text.Strong>Talk to a Sales consultant to:</text.Strong>
            </ListPretext>
            <List>
              <Li>
                <IconCheck color={colors.peach50} />
                Explore how Toggl Track is effectively utilized by teams in
                addressing challenges such as capacity planning, project and
                client profitability, and team productivity.
              </Li>
              <Li>
                <IconCheck color={colors.peach50} />
                Discover seamless methods for integrating Toggl Track into your
                team's daily workflow with minimal disruption.
              </Li>
              <Li>
                <IconCheck color={colors.peach50} />
                Seek custom solutions and receive onboarding support tailored to
                your team's requirements.
              </Li>
              <Li>
                <IconCheck color={colors.peach50} />
                Explore custom pricing options suitable for medium to
                large-sized teams.
              </Li>
            </List>

            <SocialProof>
              <text.P3>Trusted by 70,000+ teams worldwide</text.P3>
              <Responsive.DesktopOnly>
                <StaticImage
                  src='../../../assets/brands-mixed-desktop-left.png'
                  alt='Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy, Booking.com'
                  width={490}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                  objectPosition='left'
                />
              </Responsive.DesktopOnly>

              <Responsive.MobileOnly>
                <StaticImage
                  src='../../../assets/brands-mixed-desktop-left.png'
                  alt='Logos of companies that use Toggl Track: Amazon, Uber, Wise, Nielsen, KPMG, SAP, Ogilvy, Netguru, LinkedIn, GE'
                  width={350}
                  formats={['auto', 'webp', 'avif']}
                  placeholder='none'
                  objectFit='contain'
                  objectPosition='left'
                />
              </Responsive.MobileOnly>
            </SocialProof>

            <SupportContact>
              <StaticImage
                src='./assets/illo-computer-tilt.png'
                alt='A 3D illustration of a tilted computer'
                width={120}
                formats={['auto', 'webp', 'avif']}
                placeholder='none'
                objectFit='contain'
              />
              <div>
                <text.P3 color={colors.peach50}>
                  Looking for help with general questions or a technical issue?
                </text.P3>
                <Button.Secondary
                  as={Link}
                  to='/track/contact/'
                  color={colors.pink80}
                  caretDirection='right'
                >
                  Contact our Support team
                </Button.Secondary>
              </div>
            </SupportContact>
          </StyledLeft>

          <StyledRight>
            <LightbulbImage>
              <StaticImage
                src='./assets/illo-bulb.png'
                alt='A 3D illustration of a hand holding a light bulb'
                height={170}
                formats={['auto', 'webp', 'avif']}
                placeholder='none'
              />
            </LightbulbImage>
            <DemoForm>
              <DemoRequestForm />
            </DemoForm>
          </StyledRight>
        </SplitContent>
      </Hero.AltWrapper>
      <QuoteSection background={colors.darkPurple90}>
        <StyledCard>
          <StyledCardImage>
            <StaticImage
              src='./assets/cs-roguemark-tilt.png'
              alt='Team photo of Roguemar Studios, a creative agency based in California'
              width={250}
              formats={['auto', 'webp', 'avif']}
              placeholder='none'
              objectFit='contain'
            />
          </StyledCardImage>
          <div>
            <text.H3 color={colors.peach50}>
              “Toggl Track has given us actual data about our labor; we're able
              to see how much time each step of our creative process takes.”
            </text.H3>
            <text.P3 color={colors.peach50}>
              —{' '}
              <Link to='/track/customers/roguemark-studios/'>
                RogueMark Studios, Creative Agency
              </Link>
            </text.P3>
          </div>
        </StyledCard>
      </QuoteSection>
      <Reviews
        image={
          <StaticImage
            src='../../../assets/reviews-badge-desktop.png'
            alt='Toggl Track reviews: 4.5 out of 5 stars in Capterra, GetApp, Software Advice and GCrowd'
            width={1200}
            formats={['auto', 'webp', 'avif']}
            placeholder='none'
            objectFit='contain'
          />
        }
        mobileImage={
          <StaticImage
            src='../../../assets/reviews-badge-mobile.png'
            alt='Toggl Track reviews: 4.5 out of 5 stars in Capterra, GetApp, Software Advice and GCrowd'
            width={300}
            formats={['auto', 'webp', 'avif']}
            placeholder='none'
            objectFit='contain'
          />
        }
      />
    </LayoutTrack>
  )
}

const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${(p) => p.theme.primary};
`

const SplitContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  gap: 20px;
  ${media.mq[1]} {
    display: flex;
  }
  ${media.mq[2]} {
    padding-bottom: 40px;
  }
  li {
    color: ${colors.peach50};
  }
`

const Li = styled.li`
  ${text.paragraph2};
  position: relative;
  padding-left: 30px;
  &:not(:last-child) {
    margin-bottom: 25px;
  }
  svg {
    position: absolute;
    top: 5px;
    left: 0;
  }
`

const ListPretext = styled(text.P2)`
  margin: 45px 0 30px;
`

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
`

const StyledRight = styled.div`
  position: relative;
  flex: 1;
  p {
    margin: 10px auto 0;
  }
`

const StyledLeft = styled.div`
  padding: 45px;
  ${media.mq[1]} {
    flex: 1;
  }
  ${media.mq[2]} {
    padding-right: 90px;
  }
`
const LightbulbImage = styled.div`
  @media (max-width: 520px) {
    display: none;
  }
  position: absolute;
  left: 75%;
  top: -90px;
  transform: rotate(55deg);
  .gatsby-image-wrapper {
    max-width: 100px;
  }

  ${media.mq[1]} {
    left: -80px;
    top: 80px;
    transform: none;
  }
  ${media.mq[2]} {
    transform: translate(-21%, 35%);
    .gatsby-image-wrapper {
      max-width: 135px;
    }
  }
  + div {
    position: relative;
  }
`

const DemoForm = styled.div`
  position: relative;
  z-index: 10;
  ${media.mq[1]} {
    margin-top: 40px;
  }
`

const SocialProof = styled.div`
  margin: 60px 0;
  p {
    ${text.defaultText};
    text-transform: uppercase;
    color: ${colors.orange10};
    letter-spacing: 0.5px;
    opacity: 70%;
    margin-bottom: 30px;
  }
  ${media.mq[2]} {
    margin: 75px 0;
  }
`

const SupportContact = styled.div`
  p {
    ${text.defaultText};
    margin-bottom: 5px;
  }

  margin: 25px 0;
  display: flex;
  align-items: center;
  gap: 25px;
  ${media.mq[2]} {
    padding-right: 45px;
  }
`

const QuoteSection = styled(SectionBlock)`
  ${media.mq[1]} {
    padding: 45px;
  }
`
const StyledCard = styled.div`
  padding: 15px;
  h3 {
    margin-bottom: 20px;
  }
  a {
    text-decoration: underline;

    &:hover {
      color: ${(p) => p.theme.primary};
    }
  }
  ${media.mq[1]} {
    display: flex;
    flex-direction: row-reverse;
    gap: 30px;
    align-items: center;
  }
  ${media.mq[2]} {
    gap: 50px;
    max-width: 1100px;
    margin: auto;
    h3 {
      margin-bottom: 30px;
    }
  }
`

const StyledCardImage = styled.div`
  width: 45%;
  max-width: 220px;
  margin: 10px 0 30px;
  ${media.mq[1]} {
    max-width: 350px;
    margin: 0;
  }
`

export default DemoRequestPage
